import { Component, ElementRef, HostListener, Injector, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild, afterNextRender, computed, inject, input, output, signal } from '@angular/core'
import { form } from '@angular/forms/signals'
import { PageEvent } from '@angular/material/paginator'
import { LaskuAsiakkaatHakuPyynto, LaskuAsiakkaatHakuVastaus, LaskunAsiakas, LaskunAsiakastyyppi, LaskunLahetystapa, LaskunTyyppiavain } from '@lemon/core-firebase/model/lasku'
import { EMPTY, Subject, from } from 'rxjs'
import { debounceTime, map, switchMap, takeUntil } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../../_angular/service/firebase-lemonaid.service'
import { arvonMuutokset, asetaArvo } from '../../../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { osuikoKlikkausAlueelle, vaihdaValinta } from '../../../_jaettu-angular/laskut/haku/lasku-haku-logiikka'
import { KonfiguraatioPalvelu } from '../../../_jaettu-angular/service/lasku/configuration.service'
import { AsiakasDialogiService } from '../asiakas-dialogi.service'

type AsiakasHakuPaneeli = 'tyyppi' | 'myynninLaji' | 'toimitustapa' | 'paikkakunta'

interface AsiakastyyppiVaihtoehto {
  tyyppi: LaskunAsiakastyyppi
  nimiAvain: string
}

interface ToimitustapaVaihtoehto {
  lahetystapa: LaskunLahetystapa
  nimiAvain: string
}

interface NaytettavaAsiakas extends LaskunAsiakas {
  toimitusteksti: string
}

@Component({
  selector: 'app-asiakasrekisteri-haku',
  templateUrl: './asiakasrekisteri-haku.component.html',
  styleUrls: ['./asiakasrekisteri-haku.component.css'],
  standalone: false,
})
export class AsiakasrekisteriHakuComponent implements OnInit, OnChanges, OnDestroy {
  readonly avoinna = input(false)
  readonly suljettu = output<void>()

  @ViewChild('hakukentta', { read: ElementRef }) set hakukentta(kentta: ElementRef<HTMLInputElement> | undefined) {
    if (kentta) {
      afterNextRender(() => kentta.nativeElement.focus(), { injector: this._injector })
    }
  }

  readonly hakuteksti = form(signal(''))
  readonly hakusana = computed(() => this.hakuteksti().value())
  readonly tulokset = signal<NaytettavaAsiakas[]>([])
  readonly haetaan = signal(false)
  readonly kokonaismaara = signal(0)
  readonly sivu = signal(0)
  readonly sivunKoko = signal(50)
  readonly sivukoot = [50, 100, 200]

  readonly avoinPaneeli = signal<AsiakasHakuPaneeli | null>(null)

  readonly kaikkiTyypit: AsiakastyyppiVaihtoehto[] = [
    { tyyppi: LaskunAsiakastyyppi.YRITYS, nimiAvain: 'asiakkaat.haku.tyyppi-yritys' },
    { tyyppi: LaskunAsiakastyyppi.HENKILO, nimiAvain: 'asiakkaat.haku.tyyppi-henkilo' },
  ]
  readonly valitutTyypit = signal<LaskunAsiakastyyppi[]>([])

  readonly kaikkiMyynninLajit = signal<LaskunTyyppiavain[]>([])
  readonly valitutMyynninLajit = signal<LaskunTyyppiavain[]>([])

  readonly kaikkiToimitustavat: ToimitustapaVaihtoehto[] = [
    { lahetystapa: LaskunLahetystapa.SAHKOINEN, nimiAvain: 'lasku.asiakas-dialog.toimitustapa.verkkolasku' },
    { lahetystapa: LaskunLahetystapa.SAHKOPOSTI, nimiAvain: 'lasku.asiakas-dialog.toimitustapa.sahkoposti' },
    { lahetystapa: LaskunLahetystapa.ITSE, nimiAvain: 'lasku.asiakas-dialog.toimitustapa.itse' },
  ]
  readonly valitutToimitustavat = signal<LaskunLahetystapa[]>([])

  readonly kaikkiPaikkakunnat = signal<string[]>([])
  readonly valitutPaikkakunnat = signal<string[]>([])
  readonly paikkakuntaRajaus = form(signal(''))
  readonly rajatutPaikkakunnat = computed(() => this._rajaaPaikkakunnat())

  readonly onkoHakuehtoja = computed(
    () => !!this.hakuteksti().value().trim() || this.valitutTyypit().length > 0 || this.valitutMyynninLajit().length > 0 || this.valitutToimitustavat().length > 0 || this.valitutPaikkakunnat().length > 0,
  )

  private readonly _injector = inject(Injector)
  private _hakuTrigger = new Subject<void>()
  private _edellinenHakuJson: string | null = null
  private _ngUnsubscribe = new Subject<void>()

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _asiakasDialogiService: AsiakasDialogiService,
    private _konfiguraatioPalvelu: KonfiguraatioPalvelu,
  ) {
    arvonMuutokset(this.hakuteksti).subscribe(() => this.hakutekstiMuuttui())
  }

  ngOnInit() {
    this._konfiguraatioPalvelu.tyypitObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(tyypit => {
      this.kaikkiMyynninLajit.set((tyypit ?? []).map(tyyppi => tyyppi.avain))
    })

    this._hakuTrigger
      .pipe(
        debounceTime(500),
        switchMap(() => {
          if (!this.onkoHakuehtoja()) {
            this.haetaan.set(false)
            this.tulokset.set([])
            this.kokonaismaara.set(0)
            this._edellinenHakuJson = null
            return EMPTY
          }
          const pyynto = this._rakennaHakupyynto()
          const pyyntoJson = JSON.stringify(pyynto)
          if (pyyntoJson === this._edellinenHakuJson) {
            this.haetaan.set(false)
            return EMPTY
          }
          return from(this._suoritaHaku(pyynto)).pipe(map(vastaus => ({ vastaus, pyyntoJson })))
        }),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(({ vastaus, pyyntoJson }) => this._kasitteleHakuvastaus(vastaus, pyyntoJson))
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes.avoinna) {
      return
    }
    if (changes.avoinna.currentValue) {
      this._hakuTrigger.next()
    } else {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this.avoinPaneeli.set(null)
      // Nollaus varmistaa, että uudelleen avattaessa haetaan tuoreet tulokset samoillakin ehdoilla
      this._edellinenHakuJson = null
    }
  }

  @HostListener('window:keydown.escape')
  suljeEscapella() {
    if (!this.avoinna()) {
      return
    }
    if (this.avoinPaneeli()) {
      this.avoinPaneeli.set(null)
    } else {
      this.sulje()
    }
  }

  @HostListener('document:click', ['$event'])
  dokumenttiaKlikattu(event: MouseEvent) {
    if (this.avoinPaneeli() && !osuikoKlikkausAlueelle(event, ['dropdown-wrap'])) {
      this.avoinPaneeli.set(null)
    }
  }

  vaihdaPaneeli(paneeli: AsiakasHakuPaneeli) {
    this.avoinPaneeli.update(avoin => (avoin === paneeli ? null : paneeli))
    const paikkakunnatLataamatta = this.avoinPaneeli() === 'paikkakunta' && this.kaikkiPaikkakunnat().length === 0
    if (paikkakunnatLataamatta) {
      this._lataaPaikkakunnat()
    }
  }

  vaihdaTyyppi(tyyppi: LaskunAsiakastyyppi) {
    this.valitutTyypit.update(valitut => vaihdaValinta(valitut, tyyppi))
    this.hakutekstiMuuttui()
  }

  tyhjennaTyypit() {
    this.valitutTyypit.set([])
    this.hakutekstiMuuttui()
  }

  vaihdaMyynninLaji(myynninLaji: LaskunTyyppiavain) {
    this.valitutMyynninLajit.update(valitut => vaihdaValinta(valitut, myynninLaji))
    this.hakutekstiMuuttui()
  }

  tyhjennaMyynninLajit() {
    this.valitutMyynninLajit.set([])
    this.hakutekstiMuuttui()
  }

  vaihdaToimitustapa(lahetystapa: LaskunLahetystapa) {
    this.valitutToimitustavat.update(valitut => vaihdaValinta(valitut, lahetystapa))
    this.hakutekstiMuuttui()
  }

  tyhjennaToimitustavat() {
    this.valitutToimitustavat.set([])
    this.hakutekstiMuuttui()
  }

  vaihdaPaikkakunta(paikkakunta: string) {
    this.valitutPaikkakunnat.update(valitut => vaihdaValinta(valitut, paikkakunta))
    this.hakutekstiMuuttui()
  }

  tyhjennaPaikkakunnat() {
    this.valitutPaikkakunnat.set([])
    this.hakutekstiMuuttui()
  }

  tyhjennaHakuteksti() {
    asetaArvo(this.hakuteksti, '', { ilmoita: false })
    this.hakutekstiMuuttui()
  }

  tyhjennaKaikki() {
    this.valitutTyypit.set([])
    this.valitutMyynninLajit.set([])
    this.valitutToimitustavat.set([])
    this.valitutPaikkakunnat.set([])
    this.tyhjennaHakuteksti()
  }

  sulje() {
    this.suljettu.emit()
  }

  taustaaKlikattu(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.sulje()
    }
  }

  hakutekstiMuuttui() {
    this.sivu.set(0)
    this._hakuTrigger.next()
  }

  sivuMuuttui(event: PageEvent) {
    this.sivu.set(event.pageIndex)
    this.sivunKoko.set(event.pageSize)
    this._hakuTrigger.next()
  }

  async avaaAsiakas(asiakas: LaskunAsiakas) {
    const muuttui = await this._asiakasDialogiService.avaaAsiakasrekisterinAsiakas(asiakas)
    if (muuttui) {
      this._edellinenHakuJson = null
      this._hakuTrigger.next()
    }
  }

  private async _suoritaHaku(pyynto: LaskuAsiakkaatHakuPyynto): Promise<LaskuAsiakkaatHakuVastaus | null> {
    this.haetaan.set(true)
    try {
      return await this._firebaseLemonaid.functionsCall<LaskuAsiakkaatHakuPyynto, LaskuAsiakkaatHakuVastaus>('laskutEtsiAsiakas', pyynto, undefined, 'europe-north1')
    } catch {
      return null
    }
  }

  private async _lataaPaikkakunnat() {
    const pyynto: LaskuAsiakkaatHakuPyynto = { ylaraja: 1, sivu: 0 }
    const vastaus = await this._firebaseLemonaid.functionsCall<LaskuAsiakkaatHakuPyynto, LaskuAsiakkaatHakuVastaus>('laskutEtsiAsiakas', pyynto, undefined, 'europe-north1').catch(() => null)
    this.kaikkiPaikkakunnat.set(vastaus?.paikkakunnat ?? [])
  }

  private _kasitteleHakuvastaus(vastaus: LaskuAsiakkaatHakuVastaus | null, pyyntoJson: string) {
    this.haetaan.set(false)
    if (!vastaus || vastaus.e) {
      this.tulokset.set([])
      this.kokonaismaara.set(0)
      this._edellinenHakuJson = null
      return
    }
    this._edellinenHakuJson = pyyntoJson
    this.tulokset.set((vastaus.asiakkaat ?? []).map(asiakas => ({ ...asiakas, toimitusteksti: this._asiakasDialogiService.annaToimitusteksti(asiakas) })))
    this.kokonaismaara.set(vastaus.kokonaismaara ?? 0)
    this.kaikkiPaikkakunnat.set(vastaus.paikkakunnat ?? [])
  }

  private _rakennaHakupyynto(): LaskuAsiakkaatHakuPyynto {
    const pyynto: LaskuAsiakkaatHakuPyynto = { ylaraja: this.sivunKoko(), sivu: this.sivu() }
    const teksti = this.hakuteksti().value().trim()
    if (teksti) {
      pyynto.vapaahaku = teksti
    }
    if (this.valitutTyypit().length > 0) {
      pyynto.asiakastyypit = this.valitutTyypit()
    }
    if (this.valitutMyynninLajit().length > 0) {
      pyynto.laskunTyypit = this.valitutMyynninLajit()
    }
    if (this.valitutToimitustavat().length > 0) {
      pyynto.lahetystavat = this.valitutToimitustavat()
    }
    if (this.valitutPaikkakunnat().length > 0) {
      pyynto.paikkakunnat = this.valitutPaikkakunnat()
    }
    return pyynto
  }

  private _rajaaPaikkakunnat(): string[] {
    const rajaus = this.paikkakuntaRajaus().value().trim().toLowerCase()
    if (!rajaus) {
      return this.kaikkiPaikkakunnat()
    }
    return this.kaikkiPaikkakunnat().filter(paikkakunta => paikkakunta.toLowerCase().includes(rajaus))
  }
}
